import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import NotificationBoxPage from '../components/notificationBoxPage'
import apiCustomAssessment from '../services/api/customAssessment'
import IntroScreen from '../components/candidateAssessment/IntroScreen'
import ActiveScreen from '../components/candidateAssessment/ActiveScreen'
import { LoadingWrap } from '../components/candidateAssessment/styles'

export default function CandidateAssessment() {
  const { key: apiKey } = useParams()
  const { t } = useTranslation()

  const [screen, setScreen] = useState('loading')
  const [assessment, setAssessment] = useState(null)
  const [startedAt, setStartedAt] = useState(null)
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem(`answers_${apiKey}`)
    return saved ? JSON.parse(saved) : {}
  })
  const [timeLeft, setTimeLeft] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [validationError, setValidationError] = useState('')
  const [uploadingCount, setUploadingCount] = useState(0)
  const timerRef = useRef(null)
  const handleSubmitRef = useRef(null)

  const handleUploadingState = (isUploading) => {
    setUploadingCount((prev) =>
      isUploading ? prev + 1 : Math.max(0, prev - 1),
    )
  }

  useEffect(() => {
    apiCustomAssessment
      .getCandidateAssessment(apiKey)
      .then((res) => {
        setAssessment(res.assessment || res)
        if (res.status === 'expired') {
          setScreen('expired')
        } else if (res.status === 'submitted' || res.status === 'evaluated') {
          setScreen('submitted')
        } else if (res.status === 'started') {
          if (res.startedAt) setStartedAt(res.startedAt)
          setScreen('active')
        } else {
          setScreen('intro')
        }
      })
      .catch((err) => {
        setErrorMessage(
          err?.response?.data?.message || t('assessment.somethingWrong'),
        )
        setScreen('error')
      })
  }, [apiKey, t])

  // Persistence Logic for Answers
  useEffect(() => {
    localStorage.setItem(`answers_${apiKey}`, JSON.stringify(answers))
  }, [answers, apiKey])

  // Timer Logic — startedAt from backend is the source of truth
  useEffect(() => {
    if (screen !== 'active' || !assessment?.durationMinutes) return

    const storageKey = `timer_${apiKey}`

    let endTime
    if (startedAt) {
      // Always recalculate from backend's started_at so refresh can't reset the clock
      endTime =
        new Date(startedAt).getTime() + assessment.durationMinutes * 60 * 1000
      localStorage.setItem(storageKey, endTime.toString())
    } else {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        endTime = parseInt(saved, 10)
      } else {
        endTime = Date.now() + assessment.durationMinutes * 60 * 1000
        localStorage.setItem(storageKey, endTime.toString())
      }
    }

    // If the timer already ran out (e.g. user comes back after duration elapsed), auto-submit
    if (Date.now() >= endTime) {
      localStorage.removeItem(storageKey)
      handleSubmitRef.current?.(true)
      return
    }

    setTimeLeft(Math.round((endTime - Date.now()) / 1000))

    timerRef.current = setInterval(() => {
      const remaining = Math.round((endTime - Date.now()) / 1000)
      if (remaining <= 0) {
        clearInterval(timerRef.current)
        setTimeLeft(0)
        localStorage.removeItem(storageKey)
        handleSubmitRef.current?.(true)
      } else {
        setTimeLeft(remaining)
      }
    }, 1000)

    return () => clearInterval(timerRef.current)
  }, [screen, assessment, startedAt, apiKey])

  const handleStart = () => {
    apiCustomAssessment.startAssessment(apiKey).then((res) => {
      if (res?.startedAt) setStartedAt(res.startedAt)
      setScreen('active')
    })
  }

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }

  const handleSubmit = (force = false) => {
    if (isSubmitting) return

    const questions = assessment?.questions || []

    const hasUnanswered = questions.some((q) => {
      const val = answers[q.id]
      if (val === null || val === undefined || val === '') return true
      if (Array.isArray(val) && val.length === 0) return true
      return false
    })

    if (!force) {
      if (hasUnanswered) {
        setValidationError(t('assessment.allQuestionsRequired'))
        return
      }
    } else if (hasUnanswered) {
      // Timer expired with unanswered questions — don't submit, show time-expired screen
      clearInterval(timerRef.current)
      localStorage.removeItem(`timer_${apiKey}`)
      localStorage.removeItem(`answers_${apiKey}`)
      setScreen('timeExpired')
      return
    }

    setValidationError('')
    setIsSubmitting(true)
    clearInterval(timerRef.current)

    const formattedAnswers = questions.map((q) => ({
      question_id: q.id,
      answer: answers[q.id] ?? null,
    }))

    apiCustomAssessment
      .submitAssessment(apiKey, { answers: formattedAnswers })
      .then(() => {
        setIsSubmitting(false)
        localStorage.removeItem(`timer_${apiKey}`)
        localStorage.removeItem(`answers_${apiKey}`)
        setScreen(force ? 'timeExpired' : 'submitted')
      })
      .catch(() => {
        setIsSubmitting(false)
        setScreen(force ? 'timeExpired' : 'submitted')
      })
  }

  handleSubmitRef.current = handleSubmit

  if (screen === 'loading') {
    return (
      <LoadingWrap>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('general.loading')}</span>
        </div>
      </LoadingWrap>
    )
  }

  if (screen === 'error') {
    return <NotificationBoxPage messageType="error" message={errorMessage} />
  }

  if (screen === 'expired') {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('assessment.assessmentExpiredMessage')}
      />
    )
  }

  if (screen === 'submitted') {
    return (
      <NotificationBoxPage
        messageType="success"
        message={t('assessment.assessmentSubmittedMessage')}
      />
    )
  }

  if (screen === 'timeExpired') {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('assessment.timeExpiredMessage')}
      />
    )
  }

  if (screen === 'intro') {
    return <IntroScreen assessment={assessment} onStart={handleStart} />
  }

  return (
    <ActiveScreen
      assessment={assessment}
      answers={answers}
      timeLeft={timeLeft}
      isSubmitting={isSubmitting}
      validationError={validationError}
      uploadingCount={uploadingCount}
      onAnswerChange={handleAnswerChange}
      onUploadingState={handleUploadingState}
      onSubmit={handleSubmit}
    />
  )
}
